import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Checkbox from '../components/Checkbox';
import Input from '../components/Input';
import Bank from '../assets/bank.svg';
import Email from '../assets/email.svg';
import Phone from '../assets/phone.svg';
import { bankOptions, employmentOptions } from '../data/selectOptions';
import InputSelect, { Option } from '../components/InputSelect';
import AutoComplete from '../components/AutoComplete';
import { SelectChangeEvent } from '@mui/material';
import useFormStore from '../state/useFormStore';
import { apiService } from '../utils/api';
import { clearLocalStorage } from '../utils/utils';
import Lottie from 'lottie-react';
import WaitImg from '../assets/wait.json';
import TermsModal from '../components/TermsModal';

const Wrapper = styled.div<{ disabled: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

const CheckTextClickable = styled.span`
  color: ${({ theme }) => theme.colors.background};
  cursor: pointer;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const ModalSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
`;

const ProfileCard = styled.button`
  width: 100%;
  padding: 20px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "Confirmar";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: #95d0dd;
    color: #000;
    padding: 8px;
    font-weight: 600;
    text-align: center;
    transform: translateY(100%);
    transition: transform 0.2s ease;
  }

  &:hover {
    border-color: #95d0dd;
    background: #f8f8f8;

    &::after {
      transform: translateY(0);
    }
  }

  &:focus {
    outline: none;
    border-color: #95d0dd;
    box-shadow: 0 0 0 2px rgba(149, 208, 221, 0.2);
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProfileLabel = styled.span`
  font-size: 14px;
  color: #666;
`;

const ProfileValue = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 600;
`;

const ActionButton = styled(Button)`
  margin-top: 8px !important;
`;

const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const LoadingText = styled.div`
  margin-top: 20px;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.basic2000};
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 16px;
`;

const ConfirmButton = styled(Button)`
  width: 100% !important;
  background-color: #FFF200 !important;
  color: #000 !important;
  font-weight: 600 !important;
  border-radius: 50px !important;
  padding: 12px !important;
  font-size: 16px !important;

  &:hover {
    background-color: #e6d900 !important;
  }
`;

const CancelButton = styled(Button)`
  width: 100% !important;
  background-color: transparent !important;
  color: #333 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 50px !important;
  padding: 12px !important;
  font-size: 16px !important;

  &:hover {
    background-color: #f5f5f5 !important;
  }
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #FFF200;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  svg {
    font-size: 32px;
    color: #000;
  }
`;

const ActiveRequestModal = styled(ModalContent)`
  max-width: 400px;
`;

const ModalMessage = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #666;
  margin: 16px 0;
  text-align: center;
`;

const ModalDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
  margin: 24px 0;
`;

const processArgentinianName = (fullName: string): { firstname: string; lastname: string } => {
  // Eliminar espacios extras y dividir por espacios
  const nameParts = fullName.trim().replace(/\s+/g, ' ').split(' ');
  
  // Si solo hay dos palabras, la primera es nombre y la segunda apellido
  if (nameParts.length === 2) {
    return {
      firstname: nameParts[0],
      lastname: nameParts[1]
    };
  }
  
  // Si hay 3 o más palabras
  if (nameParts.length >= 3) {
    // Las dos primeras palabras son nombres en la mayoría de los casos argentinos
    const firstname = nameParts.slice(0, 2).join(' ');
    // El resto son apellidos
    const lastname = nameParts.slice(2).join(' ');
    
    return { firstname, lastname };
  }
  
  // Si solo hay una palabra (caso poco común)
  return {
    firstname: nameParts[0],
    lastname: nameParts[0]
  };
};

const UserForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [dni, setDni] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [employment, setEmployment] = useState<string>('');
  const [bank, setBank] = useState<Option>();
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dniResults, setDniResults] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showActiveRequestModal, setShowActiveRequestModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [touched, setTouched] = useState({
    dni: false,
    email: false,
    phone: false,
    employment: false,
    bank: false
  });

  // Zustand
  const setHash = useFormStore((state) => state.setHash);
  const setNames = useFormStore((state) => state.setNames);
  const setCuilParameter = useFormStore((state) => state.setCuilParameter);
  const setEmploymentParameter = useFormStore((state) => state.setEmploymentParameter);
  const setDniData = useFormStore((state) => state.setDniData);
  const setEmailData = useFormStore((state) => state.setEmailData);
  const setPhoneData = useFormStore((state) => state.setPhoneData);
  const setBankData = useFormStore((state) => state.setBankData);
  const setFirstNameData = useFormStore((state) => state.setFirstNameData);
  const setLastNameData = useFormStore((state) => state.setLastNameData);
  const setGenderData = useFormStore((state) => state.setGenderData);
  const setBirthdateData = useFormStore((state) => state.setBirthdateData);
  const setPreOffers = useFormStore((state) => state.setPreOffers);
  const setRequestUid = useFormStore((state) => state.setRequestUid);
  const token = useFormStore((state) => state.token);
  const setToken = useFormStore((state) => state.setToken);

  // Verificar y obtener token si no existe en localStorage
  useEffect(() => {
    const checkAndGetToken = async () => {
      const storedToken = localStorage.getItem('auth_token');
      
      if (!storedToken) {
        try {
          const response = await apiService.auth.createSession();
          if (response.data?.data?.accessToken) {
            const newToken = response.data.data.accessToken;
            localStorage.setItem('auth_token', newToken);
            setToken(newToken);
          }
        } catch (error) {
          console.error('Error al obtener token:', error);
        }
      } else if (!token) {
        // Si hay token en localStorage pero no en el store, sincronizar
        setToken(storedToken);
      }
    };

    checkAndGetToken();
  }, [token, setToken]);
  // Agregar estados para errores
  const [errors, setErrors] = useState({
    dni: '',
    email: '',
    phone: '',
    employment: '',
    bank: ''
  });

  const saveHashToLocalStorage = (hash: string) => {
    localStorage.setItem('hash', hash);
  };

  const saveUserDataToLocalStorage = (hash: string, data: any) => {
    localStorage.setItem(`prestix_${hash}`, JSON.stringify(data));
  };

  const toggleCheck = () => setAcceptedTerms((t) => !t);

  // Funciones de validación
  const validateDni = (value: string) => {
    if (!value) return 'El DNI es requerido';
    if (value.length < 7 || value.length > 8) return 'El DNI debe tener 7 u 8 dígitos';
    return '';
  };

  const validateEmail = (value: string) => {
    if (!value) return 'El email es requerido';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'El formato del email no es válido';
    return '';
  };

  const validatePhone = (value: string) => {
    if (!value) return 'El teléfono es requerido';
    if (value.length < 10) return 'El teléfono debe tener al menos 10 dígitos';
    return '';
  };

  const validateEmployment = (value: string) => {
    if (!value) return 'La situación laboral es requerida';
    return '';
  };

  const validateBank = (value?: Option) => {
    if (!value) return 'El banco es requerido';
    return '';
  };

  // Modificar los handlers existentes para incluir validación solo en blur
  const handleDniChange = (newDNI: string) => {
    const finalDNI = newDNI.replace(/\D/g, '');
    if (finalDNI.length > 8) return;
    setDni(finalDNI);
    if (touched.dni) {
      setErrors(prev => ({ ...prev, dni: validateDni(finalDNI) }));
    }
  };

  const handleEmailChange = (newEmail: string) => {
    const finalEmail = newEmail.toLowerCase().trim();
    setEmail(finalEmail);
    if (touched.email) {
      setErrors(prev => ({ ...prev, email: validateEmail(finalEmail) }));
    }
  };

  const handlePhoneChange = (newPhone: string) => {
    if (newPhone === '+54' || newPhone.length > 10) return;
    const finalPhone = newPhone.replace('+54 ', '').replace(/\D/g, '');
    setPhone(finalPhone);
    if (touched.phone) {
      setErrors(prev => ({ ...prev, phone: validatePhone(finalPhone) }));
    }
  };

  const handleNewEmployment = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setEmployment(value);
    if (touched.employment) {
      setErrors(prev => ({ ...prev, employment: validateEmployment(value) }));
    }
  };

  const handleNewBank = (_: React.SyntheticEvent<Element, Event>, value: Option | null) => {
    setBank(value || undefined);
    if (touched.bank) {
      setErrors(prev => ({ ...prev, bank: validateBank(value || undefined) }));
    }
  };

  // Handlers para el evento blur
  const handleDniBlur = () => {
    setTouched(prev => ({ ...prev, dni: true }));
    setErrors(prev => ({ ...prev, dni: validateDni(dni) }));
  };

  const handleEmailBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    setErrors(prev => ({ ...prev, email: validateEmail(email) }));
  };

  const handlePhoneBlur = () => {
    setTouched(prev => ({ ...prev, phone: true }));
    setErrors(prev => ({ ...prev, phone: validatePhone(phone) }));
  };

  const handleEmploymentBlur = () => {
    setTouched(prev => ({ ...prev, employment: true }));
    setErrors(prev => ({ ...prev, employment: validateEmployment(employment) }));
  };

  const handleBankBlur = () => {
    setTouched(prev => ({ ...prev, bank: true }));
    setErrors(prev => ({ ...prev, bank: validateBank(bank) }));
  };

  const submitForm = async () => {
    // Marcar todos los campos como touched
    setTouched({
      dni: true,
      email: true,
      phone: true,
      employment: true,
      bank: true
    });

    // Validar todos los campos
    const newErrors = {
      dni: validateDni(dni),
      email: validateEmail(email),
      phone: validatePhone(phone),
      employment: validateEmployment(employment),
      bank: validateBank(bank)
    };

    setErrors(newErrors);

    // Verificar si hay errores
    if (Object.values(newErrors).some(error => error !== '')) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.customers.search(dni);

      if (response.data && response.data.data && response.data.data.results) {
        setDniResults(response.data.data.results || []);
      setShowModal(true); 
      } else {
        console.error('Error: Respuesta de API inválida');
      }
    } catch (error) {
      console.error('Error al buscar cliente:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequest = async () => {
    try {
      // Limpiar localStorage
      clearLocalStorage();

      // Limpiar store
      useFormStore.setState({
        hash: null,
        name: null,
        cuil: null,
        employment: null,
        dni: null,
        email: null,
        phone: null,
        first_name: null,
        last_name: null,
        gender: null,
        bank: null,
        birthdate: null,
        requestUid: null,
        preOffers: null,
        token: null
      });

      // Cerrar modal y resetear formulario
      setShowActiveRequestModal(false);
      setDni('');
      setEmail('');
      setPhone('');
      setEmployment('');
      setBank(undefined);
      setAcceptedTerms(true);
    } catch (error) {
      console.error('Error al iniciar nueva solicitud:', error);
      alert('Hubo un error al iniciar una nueva solicitud. Por favor, intenta más tarde.');
    }
  };

  const handleConfirm = async (selectedPerson: any) => {
    setShowModal(false);
    setLoading(true);

    try {
      if (!bank?.value) {
        throw new Error('Debes seleccionar un banco para continuar');
      }

      // Procesar firstname y lastname si son null
      let processedFirstname = selectedPerson.firstname;
      let processedLastname = selectedPerson.lastname;

      if (!processedFirstname || !processedLastname) {
        const processedName = processArgentinianName(selectedPerson.name);
        processedFirstname = processedName.firstname;
        processedLastname = processedName.lastname;
      }

      // Crear la solicitud de crédito
      try {
        const response = await apiService.requests.create({
          personalInfo: {
            firstname: processedFirstname,
            lastname: processedLastname,
            gender: selectedPerson.gender,
            birthdate: selectedPerson.birthdate,
            cuil: selectedPerson.cuit,
          },
          employmentInfo: {
            employmentStatus: employment,
            bank: bank.value,
          },
          addressContact: {
            mobile: phone,
            email: email,
          }
        });

        if (!response.data?.data?.uid) {
          throw new Error('No se recibió el UID de la solicitud');
        }

        const requestUid = response.data.data.uid;
        setHash(requestUid);
        saveHashToLocalStorage(requestUid);

        // Guardar datos en el store
        setNames(selectedPerson.name);
        setCuilParameter(selectedPerson.cuit);
        setEmploymentParameter(employment);
        setDniData(dni);
        setEmailData(email);
        setPhoneData(phone);
        setFirstNameData(processedFirstname);
        setLastNameData(processedLastname);
        setGenderData(selectedPerson.gender);
        setBankData(bank.value);
        setBirthdateData(selectedPerson.birthdate);

        // Guardar preoffers si existen
        if (response.data.data.preOffers) {
          setPreOffers(response.data.data.preOffers);
        }

        // Guardar requestUid
        setRequestUid(requestUid);

        // Guardar en localStorage
        const userData = {
          hash: requestUid,
          requestUid,
          dni,
          email,
          phone,
          employment,
          bank: bank.value,
          submittedAt: new Date().toISOString(),
          birthdate: selectedPerson.birthdate,
          cuit: selectedPerson.cuit,
          gender: selectedPerson.gender,
          name: selectedPerson.name,
          first_name: processedFirstname,
          very_first_name: processedFirstname.split(' ')[0],
          last_name: processedLastname,
          preOffers: response.data.data.preOffers || []
        };

        saveUserDataToLocalStorage(requestUid, userData);

        // Navegar a la pantalla de espera
        // navigate(`/${requestUid}/espera`); // Pre offer!
        navigate(`/${requestUid}/buscando`);
      } catch (error: any) {
        if (error.response?.data?.error?.code === 'REQUEST003') {
          setShowActiveRequestModal(true);
          return;
        }
        throw error;
      }

    } catch (error) {
      console.error('Error al crear la solicitud:', error);
      alert(error instanceof Error ? error.message : 'Error al crear la solicitud');
    } finally {
    setLoading(false);
    }
  };

  return (
    <Wrapper disabled={loading}>
      {loading && (
        <LoadingOverlay>
          <Lottie animationData={WaitImg} height={150} width={150} style={{ width: '200px' }} />
          <LoadingText>
            Estamos creando tu solicitud...
            <br />
            Por favor, espera un momento
          </LoadingText>
        </LoadingOverlay>
      )}
      <Input 
        label="DNI" 
        value={dni} 
        handleChange={handleDniChange}
        onBlur={handleDniBlur}
        pattern="\d*" 
        inputMode="numeric" 
        type="tel" 
        disabled={loading}
        error={errors.dni}
      />
      <Input 
        label="Tu email" 
        value={email} 
        handleChange={handleEmailChange}
        onBlur={handleEmailBlur}
        icon={Email}
        error={errors.email}
      />
      <Input 
        label="Tu teléfono sin 0 y sin +54" 
        value={phone} 
        handleChange={handlePhoneChange}
        onBlur={handlePhoneBlur}
        icon={Phone} 
        pattern="\d*" 
        inputMode="numeric" 
        type="tel" 
        disabled={loading}
        error={errors.phone}
      />
      <AutoComplete 
        options={bankOptions} 
        value={bank} 
        handleChange={handleNewBank}
        onBlur={handleBankBlur}
        placeholder="Seleccioná tu banco" 
        icon={Bank} 
        disabled={loading}
        error={errors.bank}
      />
      <InputSelect 
        options={employmentOptions} 
        value={employment} 
        handleChange={handleNewEmployment}
        onBlur={handleEmploymentBlur}
        placeholder="Situación laboral" 
        disableClearable 
        disabled={loading}
        error={errors.employment}
      />

      <Checkbox
        checked={acceptedTerms}
        handleChange={toggleCheck}
        disabled={loading}
        text={<><span>Acepto los </span><CheckTextClickable onClick={() => setShowTermsModal(true)}><strong style={{ color: "#95d0dd" }}>términos y condiciones</strong></CheckTextClickable></>}
      />

      <TermsModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />

      <Button
        disabled={!acceptedTerms || !dni || !email || !phone || !employment || !bank || loading}
        backgroundColor={loading ? "#ccc" : theme.colors.background}
        textColor="#000000"
        text={loading ? "Enviando..." : "Continuar"}
        onClick={submitForm}
      />

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            {dniResults.length > 0 ? (
              <>
                <ModalTitle>Confirma tu identidad</ModalTitle>
                {dniResults.length > 1 ? (
                  <ModalSubtitle>
                    Encontramos más de una persona con este DNI. Por favor, selecciona tu perfil:
                  </ModalSubtitle>
                ) : (
                  <ModalSubtitle>
                    Por favor, verifica que estos sean tus datos:
                  </ModalSubtitle>
                )}

            {dniResults.map((person, index) => (
                  <ProfileCard
                    key={index}
                  onClick={() => handleConfirm(person)} 
                    type="button"
                  >
                    <ProfileInfo>
                      <ProfileLabel>Nombre completo</ProfileLabel>
                      <ProfileValue>{person.name}</ProfileValue>
                    </ProfileInfo>
                    
                    <ProfileInfo>
                      <ProfileLabel>DNI</ProfileLabel>
                      <ProfileValue>{person.dni}</ProfileValue>
                    </ProfileInfo>
                    
                    <ProfileInfo>
                      <ProfileLabel>Fecha de nacimiento</ProfileLabel>
                      <ProfileValue>{formatDate(person.birthdate)}</ProfileValue>
                    </ProfileInfo>
                  </ProfileCard>
                ))}

                {dniResults.length > 1 ? (
                  <ActionButton
                    text="No soy ninguna de estas personas"
                    backgroundColor="transparent"
                    textColor="#333"
              onClick={() => setShowModal(false)}
            />
                ) : (
                  <ButtonsContainer>
                    <ConfirmButton
                      text="Sí, soy yo"
                      onClick={() => handleConfirm(dniResults[0])}
                      textColor="#000"
                      backgroundColor="#FFF200"
                    />
                    <CancelButton
                      text="No soy yo"
                      onClick={() => setShowModal(false)}
                      textColor="#333"
                    />
                  </ButtonsContainer>
                )}
              </>
            ) : (
              <>
                <ErrorIcon>
                  <span role="img" aria-label="warning" style={{ fontSize: '32px' }}>⚠️</span>
                </ErrorIcon>
                <ModalTitle>No encontramos tus datos</ModalTitle>
                <ModalSubtitle>
                  No pudimos encontrar una persona registrada con el DNI {dni}. Por favor:
                </ModalSubtitle>
                <ul style={{ 
                  textAlign: 'left', 
                  margin: '20px 0', 
                  padding: '0 20px',
                  color: '#666',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  <li>Verifica que el número de DNI esté correcto</li>
                  <li>Asegúrate de no haber invertido ningún número</li>
                  <li>Si el problema persiste, contacta con soporte</li>
                </ul>
                <ButtonsContainer>
                  <ConfirmButton
                    text="Verificar DNI"
                    onClick={() => setShowModal(false)}
                    textColor="#000"
                    backgroundColor="#FFF200"
                  />
                </ButtonsContainer>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {showActiveRequestModal && (
        <ModalOverlay>
          <ActiveRequestModal>
            <ModalTitle>Ya existe una solicitud activa</ModalTitle>
            <ModalMessage>
              Detectamos que ya existe una solicitud de crédito activa en este dispositivo.<br />
              ¿Qué deseas hacer?
            </ModalMessage>

            <ButtonsContainer>
              <ConfirmButton
                text="Iniciar nueva solicitud"
                onClick={handleNewRequest}
                textColor="#000"
                backgroundColor="#FFF200"
              />
              <ModalDivider />
              <ModalMessage style={{ fontSize: '14px' }}>
                Si ya tienes una solicitud en proceso y deseas consultarla, te contactaremos pronto con novedades.
              </ModalMessage>
              <CancelButton
                text="Volver al inicio"
                onClick={() => {
                  setShowActiveRequestModal(false);
                  navigate('/');
                }}
                textColor="#333"
              />
            </ButtonsContainer>
          </ActiveRequestModal>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default UserForm;
